import React, { FC, useCallback, useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Wrap,
  Nav,
  Underlay,
  StyledMenu,
  Divider,
  TopBar,
  AuthButtons,
  UserInfo,
  Avatar,
  UserInfoLink,
  UserInfoName,
  StyledArrow,
} from './styled';

import { ActionButton } from '@components/Buttons/ActionButton';
import { Button, CloseButton } from '@components/Buttons';
import { logOut } from '@store/auth/actions';
import { fetchUserStart, cleanUser } from '@store/user/actions';
import { userId as userIdSelector, isLogin as isAuthenticatedSelector } from '@store/auth/selectors';
import { userDataSelector } from '@store/user/selectors';
import { resetPromotionsState } from '@store/promotions/actions';
import { clearOrders, saveSelectedOrder } from '@store/orders/actions';
import { resetApp } from '@store/app/actions';

import { ReactComponent as LogoutIcon } from '@assets/icons/logout_danger.svg';

import useOutsideClick from '@hooks/useOutsideClick';
import useWindowHeight from '@hooks/useWindowHeight';
import Auth from '@components/Auth';
import { FIRST_CHAR } from '@utils';
import { setExpirationDate, setRefreshToken, setToken } from '@utils/localStorage';
import loginClient from '@services/OAuthServices/loginClient';
import { calculateExpirationTime } from '@utils/date';
import history from '@utils/history';
import { COUPONS_LIST, PROFILE, PROMOTIONS, RECOVER_CREDITS } from '@utils/paths';
import { clearRouter } from '@store/router/actions';

interface Context {
  isOpen: boolean;
  toggleMenu: (isOpen?: boolean) => any;
}

const MenuContext = React.createContext<Context>({
  isOpen: false,
  toggleMenu: () => {},
});

const MenuProvider: FC = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = useCallback((isMenuOpen?: boolean) => {
    setIsOpen((prevState) => (isMenuOpen !== undefined ? isMenuOpen : !prevState));
  }, []);

  return (
    <MenuContext.Provider
      value={{
        isOpen,
        toggleMenu,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

const useMenu = () => {
  return {
    ...useContext(MenuContext),
  };
};

const Menu = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ref = useRef(null);
  const { isOpen, toggleMenu } = useMenu();
  const [isOpenAuthModal, setIsAuthModal] = useState(false);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const userId = useSelector(userIdSelector);
  const user = useSelector(userDataSelector);
  const windowHeight = useWindowHeight();
  const hasId = userId !== undefined;
  const userInfoPayload = {
    src: user?.photo,
    screenName: `${user?.firstName} ${user?.lastName}`,
    initials: `${user?.firstName?.charAt(FIRST_CHAR).toUpperCase()}${user?.lastName?.charAt(FIRST_CHAR).toUpperCase()}`,
  };

  const handleSignUp = () => {
    history.push('/sign-up');
    toggleMenu(false);
  };
  const handleSignIn = () => {
    history.push('/sign-in');
    toggleMenu(false);
  };

  const handleLogout = () => {
    dispatch(fetchUserStart());
    loginClient()
      .then((token) => {
        const { token_type, access_token, expires_in } = token;
        const tokenRefreshed = `${token_type} ${access_token}`;
        const expirationDate = calculateExpirationTime(expires_in);
        setToken(tokenRefreshed);
        setExpirationDate(`${expirationDate}`);
        setRefreshToken('');
        dispatch(resetPromotionsState());
        dispatch(clearOrders());
        dispatch(saveSelectedOrder(undefined));
        dispatch(logOut());
        dispatch(cleanUser());
        dispatch(clearRouter());
        dispatch(resetApp());
        toggleMenu(false);
        return history.push('/');
      })
      .catch(() => {
        setToken('');
        setExpirationDate('');
        setRefreshToken('');
        dispatch(resetPromotionsState());
        dispatch(clearOrders());
        dispatch(saveSelectedOrder(undefined));
        dispatch(logOut());
        dispatch(cleanUser());
        dispatch(clearRouter());
        dispatch(resetApp());
        toggleMenu(false);
        return history.push('/');
      });
  };

  const handleClick = (to: string) => {
    history.push(to);
    toggleMenu(false);
  };

  useOutsideClick(ref, () => toggleMenu(false), ['menu-closable']);

  return (
    <Wrap isOpen={isOpen} windowHeight={windowHeight}>
      <Auth isOpen={isOpenAuthModal} onClose={() => setIsAuthModal(false)} />
      <Underlay isOpen={isOpen} />
      <StyledMenu ref={ref} isOpen={isOpen}>
        <div>
          <TopBar>
            {isAuthenticated && hasId ? (
              <UserInfo>
                <Avatar imageUrl={userInfoPayload.src} initials={userInfoPayload.initials} />
                <div>
                  <UserInfoName>{userInfoPayload?.screenName}</UserInfoName>
                  <UserInfoLink to={PROFILE} className="menu-closable" data-test="btn-profile">
                    {t('aside.myAccount')}
                  </UserInfoLink>
                </div>
              </UserInfo>
            ) : (
              <AuthButtons>
                <Button data-test="btn-login" className="menu-closable" $type="secondary" onClick={handleSignIn}>
                  Sign in
                </Button>
                <Button data-test="btn-sign-up" className="menu-closable" onClick={handleSignUp}>
                  Sign up
                </Button>
              </AuthButtons>
            )}
            <CloseButton onClick={() => toggleMenu()} />
          </TopBar>
          {isAuthenticated && hasId && (
            <Nav>
              <Divider />
              <ActionButton size="small" icon={<StyledArrow />} onClick={() => handleClick(PROMOTIONS)}>
                {t('aside.promotions')}
              </ActionButton>
              <ActionButton size="small" icon={<StyledArrow />} onClick={() => handleClick(COUPONS_LIST)}>
                {t('aside.coupons')}
              </ActionButton>
              <ActionButton size="small" icon={<StyledArrow />} onClick={() => handleClick(RECOVER_CREDITS)}>
                {t('aside.evaluations')}
              </ActionButton>
              <Divider />
              <ActionButton
                color="#E44448"
                data-test="btn-logout"
                size="small"
                icon={<LogoutIcon />}
                onClick={() => handleLogout()}
              >
                {t('aside.logout')}
              </ActionButton>
            </Nav>
          )}
        </div>
      </StyledMenu>
    </Wrap>
  );
};

export { Menu as default, useMenu, MenuContext, MenuProvider };
